/**
 * useAdvanceFlow — the stage-advance / terminal-complete choreography
 * (UX-11 PR-2), extracted from WorkItemDrawer. It spans the header's
 * "Next:" callout, the checklist's "Done · advance" checkpoint, and two
 * of the confirm dialogs, so it lives container-level as a hook rather
 * than inside any one section.
 *
 * Behavior (verbatim from the container):
 * - Gate pre-check: advancing into Ready-for-Assignment+ needs every
 *   is_gate item done — name the unfinished one instead of
 *   soft-nag-then-move (the server would roll back atomically → a
 *   confusing no-op). Never the word "gate" in copy.
 * - Soft-nag on earlier stages: a STYLED confirm naming the unfinished
 *   required non-gate LEAF items (parents are roll-ups; leaves are the
 *   real work).
 * - Terminal complete (Design B): close-as-completed with the SAME
 *   soft-nag — the Assigned steps are NUDGES, not gates, and closeIntake
 *   bypasses the moveStage gate, so the nag is replicated here.
 *   'completed' is the product-level success disposition, distinct from
 *   the operator-chosen lost/declined close_reasons.
 */

import { useState } from 'react';
import type { WorkItemDetail, PipelineStageWire } from '@/lib/api';
import type { useCloseIntake, useMoveStage } from '@/lib/workspace';
import { ASSIGNED_STAGE_KEY, resolveStageMove } from '@/lib/workspaceStageMove';
import { moveBlockedMessage } from '@/components/workspace/workspaceFormat';
import { useSupportContact } from '@/hooks/useSupportContact';

export interface ConfirmAdvanceState {
  /** null → the terminal "Move to Completed" close (Design B), else a stage advance. */
  toStage: string | null;
  body: string;
}

export interface AdvanceFlowArgs {
  detail: WorkItemDetail | undefined;
  currentStageItems: WorkItemDetail['checklist'];
  currentStageParentIds: ReadonlySet<string>;
  stageLabel: string | null;
  nextStage: PipelineStageWire | null;
  readyStage: PipelineStageWire | null;
  moveStage: ReturnType<typeof useMoveStage>;
  closeIntake: ReturnType<typeof useCloseIntake>;
  /** Block C: hand off to the next owner after a successful advance. */
  onAdvanced: () => void;
  /** The advance into Client Setup of a card with no client yet: the
   *  caller opens the Client Setup form (the board's drag does the same),
   *  whose send carries auto_complete_stage. */
  onOpenClientSetup?: (toStageKey: string) => void;
}

export function useAdvanceFlow({
  detail,
  currentStageItems,
  currentStageParentIds,
  stageLabel,
  nextStage,
  readyStage,
  moveStage,
  closeIntake,
  onAdvanced,
  onOpenClientSetup,
}: AdvanceFlowArgs) {
  const contact = useSupportContact();
  const [advanceError, setAdvanceError] = useState<string | null>(null);
  const [confirmAdvance, setConfirmAdvance] = useState<ConfirmAdvanceState | null>(null);

  function doAdvance(toStageKey: string): void {
    if (!detail) return;
    // Into Client Setup, a card with no client becomes one: that is Add
    // client's form, the same one the board's drag opens.
    if (
      onOpenClientSetup &&
      resolveStageMove(toStageKey, { currentStageKey: '', hasClient: !!detail.client_id, kind: 'intake' })?.action ===
        'open-client-setup'
    ) {
      onOpenClientSetup(toStageKey);
      return;
    }
    moveStage.mutate(
      { id: detail.id, input: { to_stage: toStageKey, auto_complete_stage: true } },
      {
        onSuccess: onAdvanced,
        onError: (err) => setAdvanceError(moveBlockedMessage(err, contact)),
      },
    );
  }

  function handleAdvance(): void {
    if (!detail || !nextStage) return;
    setAdvanceError(null);
    const nextIsGated = readyStage ? nextStage.sort_order >= readyStage.sort_order : (nextStage.key === 'ready_for_assignment' || nextStage.key === ASSIGNED_STAGE_KEY);
    if (nextIsGated) {
      const undoneGate = detail.checklist.find((c) => c.is_gate && !c.is_done);
      if (undoneGate) { setAdvanceError(`Finish ${undoneGate.label} first.`); return; }
    }
    const remaining = currentStageItems.filter((c) => !c.is_optional && !c.is_gate && !c.is_done && !currentStageParentIds.has(c.id));
    if (remaining.length > 0) {
      setConfirmAdvance({ toStage: nextStage.key, body: `${stageLabel ?? 'This stage'} still needs: ${remaining.map((r) => r.label).join(', ')}. Advance anyway?` });
      return;
    }
    doAdvance(nextStage.key);
  }

  function doComplete(): void {
    if (!detail) return;
    closeIntake.mutate(
      { id: detail.id, reason: 'completed' },
      { onError: (err) => setAdvanceError(moveBlockedMessage(err, contact)) },
    );
  }

  function handleTerminalComplete(): void {
    if (!detail) return;
    setAdvanceError(null);
    const remaining = currentStageItems.filter((c) => !c.is_optional && !c.is_gate && !c.is_done && !currentStageParentIds.has(c.id));
    if (remaining.length > 0) {
      setConfirmAdvance({ toStage: null, body: `${stageLabel ?? 'This stage'} still needs: ${remaining.map((r) => r.label).join(', ')}. Mark completed anyway?` });
      return;
    }
    doComplete();
  }

  return {
    advanceError,
    setAdvanceError,
    confirmAdvance,
    setConfirmAdvance,
    doAdvance,
    handleAdvance,
    doComplete,
    handleTerminalComplete,
  };
}
